/**
 * Edition capabilities — the runtime mirror of the build edition.
 *
 * Fetches `GET /api/capabilities` (public, context-free) and exposes per-feature
 * flags. Until the request resolves — and if it fails — it falls back to the
 * compile-time edition default (`IS_TRIAL`): SaaS-only features are off in the
 * trial, on in the full product. So gating is correct with no flicker, and the
 * endpoint stays authoritative. Edition only: the per-organization gates are
 * `useEntitlement` and `useRollout`, read from `/api/auth/current`.
 */

import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { config } from '@/config.ts';
import { IS_TRIAL, type Edition } from '@/lib/edition.ts';
import type { FeatureKey } from '@/features/registry.ts';

export interface Capabilities {
  edition: Edition;
  features: Partial<Record<string, boolean>>;
}

const DEFAULT_CAPABILITIES: Capabilities = {
  edition: IS_TRIAL ? 'trial' : 'saas',
  features: {},
};

function useCapabilities(): Capabilities {
  const { data } = useQuery<Capabilities>({
    queryKey: ['capabilities'],
    queryFn: async () => {
      const res = await axios.get<Capabilities>(`${config.backendApi}/api/capabilities`);
      return res.data;
    },
    staleTime: Infinity,
    retry: false,
  });
  return data ?? DEFAULT_CAPABILITIES;
}

/**
 * Returns a predicate `(key) => boolean` for feature availability. Use this in a
 * component that checks several features (one hook call), e.g. nav rendering.
 */
export function useFeatureFlags(): (key: FeatureKey) => boolean {
  const caps = useCapabilities();
  return (key: FeatureKey) => {
    const v = caps.features[key];
    return typeof v === 'boolean' ? v : !IS_TRIAL;
  };
}

/** Convenience: is a single feature available in this edition/org? */
export function useFeature(key: FeatureKey): boolean {
  return useFeatureFlags()(key);
}
